import React from "react";
import {
  Divider,
  HStack,
  Skeleton,
  SkeletonText,
  Stack,
} from "@chakra-ui/react";
import ResponsiveFormStack from "../common/ResponsiveFormStack";
import {
  InputFormControl,
  PreviewDropdownFormControl,
} from "../common/FormControlComponents";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/macro";
import { useEscortFeesQuery } from "../../hooks/useEscortFeesQuery";
import { EscortFeeRequest } from "../../types/fee.types";
import CurrencyContext from "../../context/CurrencyContext";
import { formatPrice } from "../../utils/price-formatter";
import {
  ModuleChangeStatus,
  ModuleSectionHeader,
} from "../common/ModuleToggleIndicator";

interface EscortFeesProps {
  congressId: number;
  handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement>;
  setFieldValue: Function;
  fee: EscortFeeRequest;
  isEditMode?: boolean;
  moduleChange?: ModuleChangeStatus;
}

const EscortFees: React.FC<EscortFeesProps> = ({
  congressId,
  setFieldValue,
  handleChange,
  fee,
  isEditMode = false,
  moduleChange = "unchanged",
}) => {
  const { i18n } = useLingui();
  const { fees, areFeesLoading } = useEscortFeesQuery(congressId, isEditMode);
  const currency = React.useContext(CurrencyContext);

  const isSoftRequired = React.useMemo(
    () => isEditMode && moduleChange === "enabled",
    [isEditMode, moduleChange],
  );
  const hasAnyFieldFilled = !!(
    (fee.numberOfAttendants ?? 0) > 0 || fee.attendantsName?.trim()
  );
  const isCountSoftRequired = isSoftRequired && !hasAnyFieldFilled;
  const isCountHardRequired = !isSoftRequired || hasAnyFieldFilled;
  const defaultCountPreviewText = React.useMemo(() => {
    if (isSoftRequired && !hasAnyFieldFilled) {
      return i18n._("Originally not selected");
    }
    return i18n._("Please select!");
  }, [isSoftRequired, hasAnyFieldFilled, i18n]);

  const escortFees = React.useMemo(
    () =>
      fees.length > 0
        ? [
            {
              id: 0,
              leftText: i18n._("Not ordered"),
            },
            {
              id: 1,
              leftText: i18n._("1 accompanying person"),
              rightText: `${formatPrice(fees[0].totalPrice)} ${currency}`,
            },
            {
              id: 2,
              leftText: i18n._("2 accompanying person"),
              rightText: `${formatPrice(fees[0].totalPrice * 2)} ${currency}`,
            },
            {
              id: 3,
              leftText: i18n._("3 accompanying person"),
              rightText: `${formatPrice(fees[0].totalPrice * 3)} ${currency}`,
            },
          ]
        : [],
    [currency, fees, i18n, (i18n as any)._locale],
  );

  React.useEffect(() => {
    fees.length && setFieldValue("escortFee.participationFeeId", fees[0].id);
  }, [fees, setFieldValue]);

  return (
    <>
      <Stack>
        {fees.length > 0 && (
          <>
            <HStack w="full">
              <ModuleSectionHeader
                title={<Trans>Escort Fee</Trans>}
                change={moduleChange}
              />
            </HStack>
            <ResponsiveFormStack
              firstElement={
                <PreviewDropdownFormControl
                  id={"escortFees"}
                  label={i18n._("Escort Fees")}
                  values={escortFees}
                  fieldName="escortFee.numberOfAttendants"
                  setFieldValue={setFieldValue}
                  selectedId={
                    isCountSoftRequired ? undefined : fee.numberOfAttendants
                  }
                  isRequired={isCountHardRequired}
                  isSoftRequired={isCountSoftRequired}
                  defaultPreviewText={defaultCountPreviewText}
                  testId="escort-fees-dropdown"
                />
              }
              secondElement={
                fee.numberOfAttendants > 0 ? (
                  <InputFormControl
                    id="attendantsName"
                    label={i18n._("Name of accompanying person(s)")}
                    inputType="text"
                    value={fee.attendantsName}
                    name="escortFee.attendantsName"
                    onChange={handleChange}
                    isRequired
                    testId="attendants-name-input"
                  />
                ) : (
                  <></>
                )
              }
            />
          </>
        )}
        {areFeesLoading && (
          <>
            <Skeleton height="50px" />
            <SkeletonText mt="4" noOfLines={2} spacing="4" skeletonHeight="2" />
            <Skeleton height="50px" />
          </>
        )}
      </Stack>
      {fees.length > 0 && <Divider my={{ base: 4 }} />}
    </>
  );
};

export default EscortFees;
